import { useClientPortalAPI } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import { Text, Stack, Link } from "@certn/cascadia";
import {
  Avatar,
  BasicButton,
  ColumnDefinition,
  Container,
  CopyToClipboard,
  DataTable,
  MediaQuery,
  Pagination,
} from "@certn/cascadia-react";
import {
  orderingParamToSortDescriptor,
  sortDescriptorToOrderingParam,
} from "@certn/cascadia-react/components/data-tables/utils";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { ActionsDropdown } from "./components/ActionsDropdown";
import { StatusTag } from "./components/StatusTag";
import { TableDataCard } from "./components/TableDataCard";
import { GroupFilter } from "./components/filters/GroupFilter";
import { RoleFilter } from "./components/filters/RoleFilter";
import { StatusFilter } from "./components/filters/StatusFilter";
import { FilterBar } from "../../components/filters/FilterBar";
import { PageTitle } from "../../components/page-title/PageTitle";
import { useQueryParams } from "../../hooks/useQueryParams";
import { queryParamsToApiParams } from "../utils";

export function UserListView() {
  const { t } = useTranslation();
  const { params, setParam } = useQueryParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { data, mutate, isLoading } = useClientPortalAPI(
    `/api/internal/client-portal/users/`,
    queryParamsToApiParams(params)
  );
  const rows = data?.results;
  const columns: ColumnDefinition<components["schemas"]["UserList"]>[] = [
    {
      key: "auth0_full_name",
      name: t("Name"),
      isRowHeader: true,
      allowsSorting: true,
      width: "45%",
      testID: "name-column-header",
      render({ name, email }: { name: string | null; email: string | null }) {
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Avatar name={name || email} />
            <div style={{ display: "flex", flexDirection: "column" }}>
              <Text testID="user-name">{name || "--"}</Text>
              <Text
                style="global/info-label"
                color="text-weak"
                testID="user-email"
              >
                <CopyToClipboard text={email || ""} testID="copyable-email" />
              </Text>
            </div>
          </div>
        );
      },
    },
    {
      key: "roles",
      name: t("Role"),
      isRowHeader: true,
      width: "15%",
      testID: "roles-column-header",
      render({
        roles,
      }: {
        roles: readonly components["schemas"]["UserListRoles"][];
      }) {
        return (
          <Text style="form/body-secondary" testID="user-roles">
            {[...new Set(roles.map(({ name }) => name))].join(", ")}
          </Text>
        );
      },
    },
    {
      key: "customer_groups",
      name: t("Groups"),
      isRowHeader: true,
      width: "15%",
      testID: "groups-column-header",
      render({
        customer_groups: groups,
      }: {
        customer_groups: readonly components["schemas"]["UserListCustomerGroups"][];
      }) {
        return (
          <Text style="form/body-secondary" testID="user-groups">
            {[...new Set(groups.map(({ name }) => name))].join(", ")}
          </Text>
        );
      },
    },
    {
      key: "is_auth0_blocked",
      name: t("Status"),
      isRowHeader: true,
      width: "15%",
      testID: "status-column-header",
      render({ is_auth0_blocked: isNotActive }: { is_auth0_blocked: boolean }) {
        return <StatusTag isActive={!isNotActive} />;
      },
    },
    {
      key: "actions",
      isAction: true,
      width: "10%",
      render({
        id,
        name,
        email,
        is_auth0_blocked: isNotActive,
      }: {
        id: string;
        name: string | null;
        email: string | null;
        is_auth0_blocked: boolean;
      }) {
        return (
          <ActionsDropdown
            id={id}
            name={name || email}
            refreshUser={mutate}
            isActive={!isNotActive}
            showViewUser
          />
        );
      },
    },
  ];

  return (
    <Container layout="full-width">
      <PageTitle
        actions={
          <BasicButton
            testID="new-user-button"
            onPress={() => navigate(`/settings/user/create`)}
          >
            {t("New user")}
          </BasicButton>
        }
      >
        {t("Users")}
      </PageTitle>
      <MediaQuery sm type="hide">
        <Stack orientation="horizontal" gap="space-2">
          <Text color="text-weak">
            <Trans>
              Manage your team’s access levels, roles, and permissions, ensuring
              the right people have the appropriate access to your account
              features.
            </Trans>
          </Text>
          <Link
            target="_blank"
            href="https://portal.certn.co/hc/en-us/articles/31654112328211-User-permissions"
            textStyle="global/body-secondary"
            linkStyle="monochrome"
            testID="groups-learn-more-link"
          >
            <Trans>Learn more</Trans>
          </Link>
        </Stack>
      </MediaQuery>
      <FilterBar hasSearch testID="users">
        <GroupFilter />
        <RoleFilter />
        <StatusFilter />
      </FilterBar>
      <MediaQuery sm type="hide">
        <DataTable
          isLoading={isLoading}
          columns={columns}
          rows={rows ? rows : []}
          aria-label={t("Users Table")}
          testId="user-list-view-data-table"
          onRowAction={(id) => {
            navigate(`/settings/user/${id}`, {
              state: {
                last: { href: `${location.pathname}${location.search}` },
              },
            });
          }}
          onSortChange={(sortDescriptor) =>
            setParam("ordering", sortDescriptorToOrderingParam(sortDescriptor))
          }
          sortDescriptor={orderingParamToSortDescriptor(params.ordering)}
        />
      </MediaQuery>
      <MediaQuery sm type="show">
        <Container layout="full-width">
          {rows &&
            rows.map((user) => {
              return (
                <TableDataCard
                  name={user.name}
                  id={user.id}
                  email={user.email}
                  key={user.id}
                  roles={[...new Set(user.roles.map(({ name }) => name))].join(
                    ", "
                  )}
                  isActive={!user.is_auth0_blocked}
                  refreshUser={mutate}
                />
              );
            })}
        </Container>
      </MediaQuery>

      <Pagination
        setPage={(page) => setParam("page", page.toString())}
        setPageCount={(pageCount) =>
          setParam("page_size", pageCount.toString())
        }
        total={data?.count ?? 0}
        page={params.page ? Number(params.page) : 1}
        pageCount={params["page_size"] ? Number(params["page_size"]) : 25}
      />
    </Container>
  );
}
